import {observable, action, computed} from 'mobx';
import {countSite, tree2array, array2tree} from '~/routes/LandingPage/treeUtils';
import moment from 'moment';

const fmtTime = time => time.format('YYYY-MM-DD HH:mm:ss');

class LandingPageStore {
  // 从menuUlr中解析出来的参数
  @observable.ref landingPageOptions = null;

  @action setLandingPageOptions = (data) => {
    this.landingPageOptions = data;
  };

  @computed get dtvOptions() {
    return this.landingPageOptions?.dtvOptions;
  }

  @computed get dtvUrl() {
    const {menuUrl, pageId} = this;
    return menuUrl.replace(/\/[^/]+$/, `/${pageId}`);
  }

  @observable pageId = '';

  @action setPageId = (data) => {
    this.pageId = data;
  };

  // 永远保持当前菜单的原始url
  @observable menuUrl = '';

  @action setMenuUrl = (data) => {
    this.menuUrl = data || '';
  };

  // 存储每种mdmType在当前菜单下的dtvUrl
  mdmTypeDtvUrlMapping = {};

  setMdmTypeDtvUrlMapping = (data) => {
    this.mdmTypeDtvUrlMapping = {...this.mdmTypeDtvUrlMapping, ...data};
  };

  @computed get filters() {
    return this.landingPageOptions?.filters || [];
  }

  @computed get assetFilter() {
    return this.filters.find(f => f.type === 'asset');
  }

  // 从url的state中解析出来的landingPage参数
  @observable.ref landingPageState = {};

  @action setLandingPageState = (data) => {
    if (JSON.stringify(this.landingPageState) !== JSON.stringify(data)) {
      this.landingPageState = data;
    }
  };

  @computed get mdmTypes() {
    return this.assetFilter?.mdmTypes || '';
  }

  @computed get assetSticky() {
    return this.assetFilter?.assetSticky;
  }

  // 资产筛选的列表，如果有面包屑，则包含父节点、子节点，如果子节点有多个，则用逗号分隔
  @observable nowAssetIds = [];

  @action setNowAssetIds = (data) => {
    if (JSON.stringify(data) !== JSON.stringify(this.nowAssetIds)) {
      this.nowAssetIds = data;
    }
  };

  // 如果是单选，这个值是单选的结果
  @computed get singleAssetId() {
    return [...this.nowAssetIds].pop()?.split(',').shift();
  }

  @computed get treeCheckedKeys() {
    return [...this.nowAssetIds].pop()?.split(',');
  }

  @computed get showCrumbs() {
    return this.nowAssetIds.length > 1;
  }

  // tree有值的时候，assetAttributes也应该初始化值
  @observable.shallow assetAttributes = {};

  @action setAssetAttributes = (data) => {
    this.assetAttributes = {...this.assetAttributes, ...data};
  };

  @computed get singleAssetAttrs() {
    const {singleAssetId, assetAttributes} = this;
    return assetAttributes?.[singleAssetId];
  }

  @observable loadingAttribute = false;

  @action setLoadingAttribute = (data) => {
    this.loadingAttribute = data;
  };

  @observable.ref assetNodes = [];

  @action setAssetNodes(data) {
    this.assetNodes = data;
    if (data) {
      const attributes = {};
      data.filter(node => node.attributes).forEach((node) => { attributes[node.id] = node.attributes; });
      this.setAssetAttributes(attributes);
    }
  }

  @computed get treeCheckable() {
    return this.assetFilter?.checkable;
  }

  @computed get assetTree() {
    const {assetNodes, mdmTypes} = this;
    if (!assetNodes?.length) {
      return null;
    }

    const tree = array2tree(assetNodes);
    const sitesCount = countSite(tree, mdmTypes.split(','));
    let nodes = tree2array(tree);
    nodes = nodes.filter(node => sitesCount[node.id]);
    nodes.forEach(node => delete node.children);
    return nodes.length ? array2tree(nodes) : null;
  }

  @computed get nodeMappingById() {
    const mapping = {};
    this.assetNodes.forEach((n) => {
      mapping[n.id] = n;
    });
    return mapping;
  }

  dtvInstance = null;

  setDtvInstance = (data) => {
    this.dtvInstance = data;
  };

  // 是否进入dtv的编辑态
  @observable dtvEditModel = false;

  @action setDtvEditModel = (data) => {
    this.dtvEditModel = data;
  };

  @observable loadingCount = 0;

  @computed get isLoading() {
    return this.loadingCount > 0;
  }

  @action addLoading = () => {
    this.loadingCount++;
  };

  @action subtractLoading = () => {
    if (this.loadingCount > 0) {
      this.loadingCount--;
    }
  };

  @action clearStore = () => {
    this.nowAssetIds = [];
    this.loadingCount = 0;
    // this.dtvInstance = null;
  };

  @observable.ref systemTime = null;

  @action setSystemTime = (data) => {
    this.systemTime = data;
  };

  @computed get showInterval() {
    return this.timeFilter?.showInterval;
  }

  @observable interval = 'D';

  @action setInterval = (data) => { this.interval = data; };

  @computed get showRange() {
    return this.timeFilter?.showRange;
  }

  @observable queryTime = moment();

  @action setQueryTime = (queryTime) => {
    this.queryTime = queryTime;
  };

  @observable queryTimeRange = [moment().startOf('Month'), moment().endOf('month')];

  @action setQueryTimeRange = (data) => {
    this.queryTimeRange = data;
  };

  @computed get startTime() {
    const time = this.showRange ? this.queryTimeRange[0] : this.queryTime;
    return fmtTime(time.startOf('day'));
  }

  @computed get endTime() {
    const time = this.showRange ? this.queryTimeRange[1] : this.queryTime;
    return fmtTime(time.endOf('day'));
  }

  @computed get timeFilter() {
    return this.filters.find(f => f.type === 'time');
  }

  @observable editWidgetURL = null;

  @action setEditWidgetURL = (editWidgetURL) => {
    this.editWidgetURL = editWidgetURL;
  };
}

const landingPageStore = new LandingPageStore();

export default landingPageStore;
